import { ChangeDetectionStrategy, Component, computed, effect, HostListener, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import { NgScrollbarModule } from 'ngx-scrollbar';

import { AccountMenuAction, LogoutReason } from '@shared/constants';
import { HttpHelperService, LayoutService, ToasterService } from '@shared/services';
import { AuthService } from '@shared/auth';
import { AccountTenantGroup } from '@shared/models';
import { ENVIRONMENT_CONFIG, EnvironmentConfig, normalizeTenantId } from '@shared/config';
import {
  getPreferredLocale,
  isFrCaLocale,
  isLocaleMenuExpandedPreferred,
  LOCALE_SUBPATH_FR_CA,
  PreferredLocale,
  dismissLocaleSetupBanner,
  setLocaleMenuExpandedPreferred,
  switchToLocale,
  withAuth0UiLocales
} from '@shared/i18n';
import { buildCrossTenantLoadingUrl, findTenantAppTarget } from '@shared/utils';

import { PulseTopbarMenuComponent } from './pulse-topbar-menu.component';

interface LocaleOption {
  label: string;
  value: PreferredLocale;
  lang: string;
}

const LOCALE_OPTIONS: LocaleOption[] = [
  { label: 'English', value: 'en-US', lang: 'en' },
  { label: 'Français', value: 'fr-CA', lang: 'fr' }
];

/**
 * Full-width top bar with product branding and a combined account menu for profile, company context, and switching.
 */
@Component({
  selector: 'pulse-topbar',
  imports: [NgScrollbarModule, PulseTopbarMenuComponent],
  templateUrl: './pulse-topbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PulseTopbarComponent {
  private envConfig = inject<EnvironmentConfig>(ENVIRONMENT_CONFIG);
  private authService = inject(AuthService);
  private toasterService = inject(ToasterService);
  private httpHelper = inject(HttpHelperService);
  private router = inject(Router);
  layoutService = inject(LayoutService);

  /** Product brand mark shown at the far left of the bar. */
  brandLogoUrl = input.required<string>();

  /** Accessible text for the product brand mark. */
  brandLogoAltText = input<string>();

  /** Wordmark image shown beside the circle brand mark (e.g. Lumatrak Pulse logo). */
  brandWordmarkUrl = input.required<string>();

  /** Accessible text for the wordmark image. */
  brandWordmarkAltText = input<string>();

  userAccountSummary = computed(() => {
    const user = this.authService.user();

    return {
      displayName: user.getDisplayName(),
      email: user.email || '',
      companyName: user.companyName || ''
    };
  });

  userInitials = computed(() => {
    const name = this.userAccountSummary().displayName.trim();
    if (!name) {
      return '';
    }

    const parts = name.split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
    return (first + last).toUpperCase();
  });

  accountPanelOpen = signal(false);
  mobileCompanySwitcherOpen = signal(false);
  accountTenantGroups = computed<AccountTenantGroup[]>(() => this.authService.user().accountTenantGroups);
  isSwitchingAccount = signal(false);
  isChangingPassword = signal(false);
  hasTenantGroups = computed(() => this.accountTenantGroups().length > 0);
  availableEntityRelationshipCount = computed(() => this.accountTenantGroups().reduce((total, tenant) => total + tenant.companies.length, 0));
  canSwitchAccount = computed(() => !!this.envConfig.accountSwitchEnabled && this.availableEntityRelationshipCount() > 1);
  expandedTenantIds = signal<string[]>([]);

  /**
   * Whether the account menu shows the language switcher.
   * Locale switcher logic, env `localeSwitchEnabled`, and Auth0 returnTo stay unchanged.
   */
  canSwitchLocale = computed(() => !!this.envConfig.localeSwitchEnabled);

  /** Whether the active compile-time bundle is French (Canada). */
  isFrenchLocale = isFrCaLocale();

  /** Bilingual options for the language switcher (labels stay in each language). */
  localeOptions = LOCALE_OPTIONS;

  /**
   * Whether the language submenu is expanded inside the account menu.
   * Sticky via localStorage; defaults to expanded when unset.
   */
  isLocaleMenuExpanded = signal(isLocaleMenuExpandedPreferred());

  /** Extra classes for the account popover panel. */
  accountPanelClass = computed(() => (this.canSwitchAccount() ? 'account-menu account-menu--switcher' : 'account-menu account-menu--single'));

  /** Active locale id for the language switcher. */
  get selectedLocale(): PreferredLocale {
    return this.isFrenchLocale ? 'fr-CA' : 'en-US';
  }

  /** Display label for the active locale (shown next to the language chevron). */
  get selectedLocaleLabel(): string {
    return this.localeOptions.find((option) => option.value === this.selectedLocale)?.label ?? 'English';
  }

  constructor() {
    effect(() => {
      const action = this.layoutService.consumeMobileAccountMenuAction();
      if (action === AccountMenuAction.Switch) {
        if (this.layoutService.isMobileLayout()) {
          this.openMobileCompanySwitcher();
        } else {
          this.openAccountPanel();
        }
      } else if (action === AccountMenuAction.Password) {
        this.changePassword();
      }
    });
  }

  toggleMobileNav(): void {
    this.layoutService.toggleMobileNav();
  }

  onAccountMenuOpenChange(open: boolean): void {
    if (open) {
      this.openAccountPanel();
      return;
    }

    this.closeAccountPanel();
  }

  @HostListener('document:keydown.escape')
  onEscapeKeydown(): void {
    if (this.mobileCompanySwitcherOpen()) {
      this.closeMobileCompanySwitcher();
    }
  }

  closeMobileCompanySwitcherFromUi(): void {
    this.closeMobileCompanySwitcher();
  }

  onChangePassword(): void {
    this.closeAccountPanel();
    this.changePassword();
  }

  onLogout(): void {
    this.closeAccountPanel();
    this.authService.logout(LogoutReason.UserInitiated);
  }

  toggleLocaleMenu(): void {
    this.isLocaleMenuExpanded.update((open) => {
      const next = !open;
      setLocaleMenuExpandedPreferred(next);
      return next;
    });
  }

  /**
   * Saves the locale preference and full-page navigates to the selected locale bundle.
   */
  onLocaleSelected(locale: PreferredLocale): void {
    if (!this.canSwitchLocale() || locale === this.selectedLocale) {
      return;
    }

    if (locale === 'fr-CA') {
      dismissLocaleSetupBanner();
    }

    switchToLocale(locale);
  }

  isTenantExpanded(tenantId: string): boolean {
    return this.expandedTenantIds().includes(tenantId);
  }

  tenantContainsCurrentCompany(tenant: AccountTenantGroup): boolean {
    const currentRelationshipId = this.authService.user().rId;
    return tenant.companies.some((company) => company.entityRelationshipId === currentRelationshipId);
  }

  toggleTenant(tenantId: string): void {
    if (this.isSwitchingAccount()) {
      return;
    }

    if (this.isTenantExpanded(tenantId)) {
      this.expandedTenantIds.set(this.expandedTenantIds().filter((id) => id !== tenantId));
    } else {
      this.expandedTenantIds.set([...this.expandedTenantIds(), tenantId]);
    }
  }

  selectCompany(entityRelationshipId: string): void {
    if (!this.canSwitchAccount()) {
      return;
    }

    const currentEntityRelationshipId = this.authService.user().rId;

    if (this.isSwitchingAccount() || entityRelationshipId === currentEntityRelationshipId) {
      return;
    }

    const targetTenant = this.accountTenantGroups().find((tenant) => tenant.companies.some((company) => company.entityRelationshipId === entityRelationshipId));

    if (!targetTenant) {
      this.toasterService.displayError($localize`Switch Account`, $localize`Unable to switch accounts right now. Please try again.`);
      return;
    }

    this.isSwitchingAccount.set(true);
    this.closeAccountPanel();
    this.closeMobileCompanySwitcher();

    const isSameTenant = normalizeTenantId(targetTenant.tenantId) === normalizeTenantId(this.envConfig.tenantEntityId);
    if (isSameTenant) {
      this.navigateSameTenant(entityRelationshipId);
      return;
    }

    this.navigateCrossTenant(targetTenant.tenantId, entityRelationshipId);
  }

  private navigateSameTenant(entityRelationshipId: string): void {
    this.router
      .navigate(['/loading'], { queryParams: { entityRelationshipId } })
      .then((isNavigated) => {
        if (!isNavigated) {
          this.toasterService.displayError($localize`Switch Account`, $localize`Unable to switch accounts right now. Please try again.`);
          this.isSwitchingAccount.set(false);
        }
      })
      .catch((error) => {
        console.error('Error routing to account switch loading flow', error);
        this.toasterService.displayError($localize`Switch Account`, $localize`Unable to switch accounts right now. Please try again.`);
        this.isSwitchingAccount.set(false);
      });
  }

  private navigateCrossTenant(tenantId: string, entityRelationshipId: string): void {
    const target = findTenantAppTarget(this.envConfig.tenantAppOrigins, tenantId);
    if (!target) {
      this.toasterService.displayError(
        $localize`Switch Account`,
        $localize`That company is on a tenant that is not configured for switching in this environment.`
      );
      this.isSwitchingAccount.set(false);
      return;
    }

    const preferFrench = !!target.localeSwitchEnabled && getPreferredLocale() === 'fr-CA';
    const loadingPath = preferFrench ? `/${LOCALE_SUBPATH_FR_CA}/loading` : '/loading';
    // Full-page hop — do not Auth0 logout (preserves SSO session on login.lumatrak.com).
    window.location.assign(buildCrossTenantLoadingUrl(target, entityRelationshipId, loadingPath));
  }

  isCompanyCurrent(entityRelationshipId: string): boolean {
    return this.authService.user().rId === entityRelationshipId;
  }

  private openAccountPanel(): void {
    this.initializeExpandedTenants();
    this.accountPanelOpen.set(true);
  }

  private closeAccountPanel(): void {
    this.accountPanelOpen.set(false);
  }

  private openMobileCompanySwitcher(): void {
    this.initializeExpandedTenants();
    this.mobileCompanySwitcherOpen.set(true);
  }

  private closeMobileCompanySwitcher(): void {
    this.mobileCompanySwitcherOpen.set(false);
  }

  private initializeExpandedTenants(): void {
    const currentTenantId = this.getCurrentTenantId();
    this.expandedTenantIds.set(currentTenantId ? [currentTenantId] : []);
  }

  private getCurrentTenantId(): string | null {
    const currentRelationshipId = this.authService.user().rId;

    for (const tenant of this.accountTenantGroups()) {
      if (tenant.companies.some((company) => company.entityRelationshipId === currentRelationshipId)) {
        return tenant.tenantId;
      }
    }

    return this.accountTenantGroups()[0]?.tenantId ?? null;
  }

  private changePassword(): void {
    if (this.isChangingPassword()) {
      return;
    }

    this.isChangingPassword.set(true);
    this.toasterService.displayLoading($localize`Change Password`, $localize`One moment while we redirect you.`);

    this.httpHelper.get<{ success: boolean; ticketUrl?: string; errorMessage?: string }>(`${this.envConfig.authApiUrl}/Auth/PasswordChangeTicket`).subscribe({
      next: (result) => {
        this.isChangingPassword.set(false);
        this.toasterService.clearLoadingToast();

        if (result.success && result.ticketUrl) {
          this.authService.clearUser(LogoutReason.PasswordChangeTicket);
          window.location.href = withAuth0UiLocales(result.ticketUrl);
        } else {
          this.toasterService.displayError($localize`Password Change`, result?.errorMessage || $localize`Unable to start password change. Please try again.`);
        }
      },
      error: (error) => {
        this.isChangingPassword.set(false);
        this.toasterService.clearLoadingToast();
        console.error('Password change ticket error:', error);
        this.toasterService.displayError($localize`Password Change`, $localize`Unable to start password change. Please try again.`);
      }
    });
  }
}
